import React, { useState, useEffect } from 'react';

// =========================================================================
// PANTALLA DE CARGA MÉDICA — sistema contextual por sección.
// Cada acción del profesional (Dashboard, Agenda, Pacientes, Facturas,
// Monitor de Presencia…) muestra su propio icono, título y una checklist
// animada con los datos que se están recuperando, para una espera
// informativa y profesional en lugar de un spinner genérico.
//
// Uso:
//   <PantallaCargaMedica variante="agenda" />
//   <PantallaCargaMedica mensaje="Texto propio..." />   (retrocompatible)
// =========================================================================

// Iconos SVG por sección (línea, heredan currentColor)
const ICONOS = {
    dashboard: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />,
    agenda: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
    pacientes: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />,
    facturas: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
    presencia: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    consultas: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />,
    default: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 12h4l2-5 4 10 2-5h6" />
};

// Paleta por sección (anillo/icono y check de pasos)
const ACENTOS = {
    sky:     { anillo: 'bg-sky-500', fondo: 'bg-sky-100', icono: 'text-sky-600', check: 'text-sky-600', barra: 'bg-sky-500' },
    indigo:  { anillo: 'bg-indigo-500', fondo: 'bg-indigo-100', icono: 'text-indigo-600', check: 'text-indigo-600', barra: 'bg-indigo-500' },
    emerald: { anillo: 'bg-emerald-500', fondo: 'bg-emerald-100', icono: 'text-emerald-600', check: 'text-emerald-600', barra: 'bg-emerald-500' },
    blue:    { anillo: 'bg-clinica-primary', fondo: 'bg-blue-100', icono: 'text-clinica-primary', check: 'text-clinica-primary', barra: 'bg-clinica-primary' },
    amber:   { anillo: 'bg-amber-500', fondo: 'bg-amber-100', icono: 'text-amber-600', check: 'text-amber-600', barra: 'bg-amber-500' }
};

// Presets contextuales por acción. Cada paso describe una operación REAL que el
// backend ejecuta para esa sección (endpoint indicado en el comentario), para
// que la espera sea informativa y fiel a lo que ocurre en el servidor.
const VARIANTES = {
    // Dashboard → GET /consultas/agenda?fecha=hoy (se agrupa por box y se calculan KPIs en cliente)
    dashboard: {
        icono: 'dashboard', acento: 'sky',
        titulo: 'Preparando tu panel clínico',
        pasos: ['Consultando la agenda del día', 'Distribuyendo pacientes por box', 'Calculando métricas de la jornada']
    },
    // Agenda → GET /consultas/agenda + GET /profesionales
    agenda: {
        icono: 'agenda', acento: 'indigo',
        titulo: 'Abriendo la agenda médica',
        pasos: ['Cargando el cuadro de citas', 'Recuperando la plantilla de facultativos', 'Preparando la rejilla horaria']
    },
    // Pacientes → GET /pacientes (el servidor descifra DNI/teléfono/email por RGPD)
    pacientes: {
        icono: 'pacientes', acento: 'emerald',
        titulo: 'Recuperando el censo de pacientes',
        pasos: ['Consultando el directorio clínico', 'Descifrando datos protegidos (RGPD)', 'Preparando los expedientes']
    },
    // Facturas → GET /facturas
    facturas: {
        icono: 'facturas', acento: 'blue',
        titulo: 'Accediendo a facturación y caja',
        pasos: ['Cargando las facturas emitidas', 'Cuadrando importes y métodos de pago', 'Verificando la numeración fiscal']
    },
    // Monitor → GET /fichajes/reportes + GET /profesionales
    presencia: {
        icono: 'presencia', acento: 'amber',
        titulo: 'Cargando el monitor de presencia',
        pasos: ['Recuperando los fichajes del equipo', 'Cargando la plantilla de profesionales', 'Calculando horas y jornadas activas']
    },
    // Consultas → GET /consultas
    consultas: {
        icono: 'consultas', acento: 'sky',
        titulo: 'Recuperando el histórico de consultas',
        pasos: ['Cargando los actos clínicos', 'Ordenando el histórico por fecha', 'Preparando los informes']
    },
    default: {
        icono: 'default', acento: 'emerald',
        titulo: 'Accediendo al entorno clínico',
        pasos: ['Estableciendo canal seguro con el servidor', 'Sincronizando datos']
    }
};

function IconoCheck({ className = '' }) {
    return (
        <svg className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
        </svg>
    );
}

export default function PantallaCargaMedica({ variante = 'default', mensaje }) {
    const preset = VARIANTES[variante] || VARIANTES.default;
    const acento = ACENTOS[preset.acento] || ACENTOS.emerald;
    const titulo = mensaje || preset.titulo;
    const pasos = preset.pasos;

    // Avance visual de la checklist: cada paso se "completa" secuencialmente.
    // Es una afordancia de progreso (no ligada al fetch real) para una espera
    // informativa; el último paso queda activo mientras siguen llegando datos.
    const [pasoActivo, setPasoActivo] = useState(0);
    useEffect(() => {
        if (pasoActivo >= pasos.length - 1) return;
        const t = setTimeout(() => setPasoActivo((p) => Math.min(p + 1, pasos.length - 1)), 700);
        return () => clearTimeout(t);
    }, [pasoActivo, pasos.length]);

    const progreso = Math.round(((pasoActivo + 1) / pasos.length) * 100);

    return (
        <div
            className="flex flex-col items-center justify-center min-h-[60vh] w-full px-4"
            style={{ animation: 'fadeIn 0.6s ease-in-out forwards' }}
            role="status"
            aria-live="polite"
            aria-label={titulo}
        >
            {/* Icono con latido */}
            <div className="relative flex items-center justify-center w-24 h-24 mb-6">
                <div className={`absolute inset-0 ${acento.anillo} rounded-full animate-ping opacity-20`}></div>
                <div className={`absolute inset-0 ${acento.fondo} rounded-full shadow-inner`}></div>
                <svg className={`relative w-11 h-11 ${acento.icono} animate-pulse`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {ICONOS[preset.icono] || ICONOS.default}
                </svg>
            </div>

            {/* Título */}
            <h3 className="text-lg font-black text-slate-800 tracking-tight text-center">{titulo}</h3>
            <p className="text-[11px] font-black text-slate-400 mt-1.5 uppercase tracking-widest">Sincronizando datos…</p>

            {/* Barra de progreso */}
            <div className="w-full max-w-xs mt-6 mb-5">
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${acento.barra} rounded-full transition-all duration-500 ease-out`} style={{ width: `${progreso}%` }}></div>
                </div>
            </div>

            {/* Checklist de datos que se recuperan */}
            <ul className="w-full max-w-xs space-y-2.5">
                {pasos.map((paso, i) => {
                    const completado = i < pasoActivo;
                    const activo = i === pasoActivo;
                    return (
                        <li key={paso} className="flex items-center gap-3">
                            <span className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 transition-all ${
                                completado ? `${acento.fondo} ${acento.check}` : activo ? 'bg-white border-2 border-slate-200' : 'bg-slate-100'
                            }`}>
                                {completado ? (
                                    <IconoCheck />
                                ) : activo ? (
                                    <span className={`w-3 h-3 border-2 ${acento.icono} border-t-transparent rounded-full animate-spin`}></span>
                                ) : (
                                    <span className="w-1.5 h-1.5 bg-slate-300 rounded-full"></span>
                                )}
                            </span>
                            <span className={`text-sm font-bold transition-colors ${
                                completado ? 'text-slate-700' : activo ? 'text-slate-900' : 'text-slate-400'
                            }`}>
                                {paso}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
